import React from 'react';
import { Check, Info } from 'lucide-react';
import { motion } from 'framer-motion';

interface PlanPricingProps {
  name: string;
  description: string;
  originalPrice: number;
  monthlyPrice: number;
  discountPerYear: number;
  isPopular?: boolean;
  colorTheme: string; // Tailwind class like bg-blue-600
  features: string[];
  ctaText: string;
}

const plans: PlanPricingProps[] = [
  {
    name: 'Avance',
    description: 'Ideal para MEIs com operação própria que querem estruturar a gestão.',
    originalPrice: 59,
    monthlyPrice: 49,
    discountPerYear: 120,
    colorTheme: 'bg-emerald-500',
    ctaText: 'Teste 30 dias grátis',
    features: [
      'Emissão ilimitada NF-e e NFC-e',
      'Até 2.000 anúncios e produtos',
      'Importação automática de pedidos',
      'Gestão de estoque simples',
      '12 meses de retenção de dados'
    ]
  },
  {
    name: 'Construa',
    description: 'Ideal para empresas do Simples Nacional que estão crescendo rápido.',
    originalPrice: 159,
    monthlyPrice: 129,
    discountPerYear: 360,
    colorTheme: 'bg-[#7f00ff]',
    isPopular: true,
    ctaText: 'Teste 30 dias grátis',
    features: [
      'Tudo do plano Avance',
      'Até 5.000 anúncios e produtos',
      'Multiempresa',
      'Emissão de boletos e cobranças',
      '24 meses de retenção de dados'
    ]
  },
  {
    name: 'Impulsione',
    description: 'Para gestão estruturada e foco total em expansão multicanal.',
    originalPrice: 349,
    monthlyPrice: 279,
    discountPerYear: 840,
    colorTheme: 'bg-[#5b21b6]',
    ctaText: 'Teste 30 dias grátis',
    features: [
      'Tudo do plano Construa',
      'Até 10.000 anúncios e produtos',
      'Calculadora de preços e margens',
      'Planejamento de compras avançado',
      'Setup de e-commerce próprio'
    ]
  },
  {
    name: 'Domine',
    description: 'Para operações robustas que exigem alta governança fiscal e estratégia.',
    originalPrice: 849,
    monthlyPrice: 679,
    discountPerYear: 2040,
    colorTheme: 'bg-slate-900',
    ctaText: 'Fale com comercial',
    features: [
      'Tudo do plano Impulsione',
      'Até 20.000 anúncios e produtos',
      '36 meses de retenção de dados',
      'Implementação personalizada VIP',
      'Suporte direto por telefone e chat min'
    ]
  }
];

export default function OlistPricing() {
  return (
    <section className="bg-slate-50 py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-4"
          >
            As ferramentas que seu negócio precisa, <br className="hidden md:block"/>
            <span className="text-[#7f00ff]">na mesma mensalidade</span>
          </motion.h2>
          <p className="text-lg text-purple-700 font-medium bg-purple-100/50 py-2 px-6 rounded-full inline-block">
            Até 20% de desconto no plano anual
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {plans.map((plan, idx) => (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              key={idx}
              className={`bg-white rounded-[32px] p-8 shadow-sm border ${
                plan.isPopular ? 'border-[#7f00ff] shadow-[#7f00ff]/10 shadow-xl relative scale-100 xl:scale-105 z-10' : 'border-slate-200'
              } flex flex-col`}
            >
              {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#7f00ff] text-white font-bold text-sm tracking-widest uppercase px-4 py-1.5 rounded-full shadow-md">
                  MAIS ESCOLHIDO
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-4 h-4 rounded-full ${plan.colorTheme}`} />
                <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
              </div>
              
              <p className="text-slate-500 text-sm h-14">{plan.description}</p>
              
              <div className="my-6">
                <div className="flex items-end gap-2 mb-1 text-slate-400 line-through">
                  R$ {plan.originalPrice}
                </div>
                <div className="flex items-end gap-1">
                  <span className="text-slate-900 font-bold text-4xl">R${plan.monthlyPrice}</span>
                  <span className="text-slate-500 font-medium">/mês</span>
                </div>
                <p className="text-xs font-semibold text-emerald-600 mt-2 bg-emerald-50 inline-block px-3 py-1 rounded-full">
                  Economize R$ {plan.discountPerYear} no ano
                </p>
              </div>

              <div className="w-full mt-auto mb-8">
                <button className={`w-full py-4 rounded-2xl font-bold text-center transition-all ${
                  plan.isPopular 
                    ? 'bg-[#7f00ff] text-white hover:bg-[#6a00d6] shadow-lg shadow-purple-600/30' 
                    : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                }`}>
                  {plan.ctaText}
                </button>
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-100 pt-6">
                <p className="text-sm font-bold text-slate-900">O que está incluso:</p>
                {plan.features.map((feature, fIdx) => (
                  <div key={fIdx} className="flex gap-3">
                    <Check className="w-5 h-5 text-[#7f00ff] shrink-0" />
                    <span className="text-sm text-slate-600 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
